// src/pages/HrAuditsPage.tsx — HR Audits (admin / HR-admin only).
//
// One /api/hr-audits fetch drives a user search (per-person consent read/
// rejected/unread status) and seven audit tabs:
//   1 Not signed in (invited, never signed in)
//   2 Inactive 30+ days (signed in before, not for 30 days)
//   3 Emergency access (who viewed a colleague's emergency contact)
//   4 HR-admin changes (HR Admin permission granted/removed)
//   5 Unread consents      6 Rejected consents
//   7 Newsletter unread (CEO newsletter non-openers — Weekly isn't read-tracked)

import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiFetch, ApiError } from "../api";
import { useCurrentUser } from "../auth";
import { C, F } from "../theme";
import { Button, Card, Eyebrow, Hairline, PermissionLoading, SearchInput, Stamp, TopBar } from "../components/ui";

type ConsentStatus = "read" | "rejected" | "unread";
interface ConsentBrief { docId: number; name: string; status: ConsentStatus; at: string | null }
interface HrUserRow {
  id: number; name: string; email: string; departments: string[];
  authMethod: "sso" | "pin"; lastLogin: string | null; signOnStatus: string;
  isHrAdmin: boolean; isAdmin: boolean; isBlocked: boolean; createdAt: string | null;
  consents: ConsentBrief[];
}
interface AuditRow { id: number; at: string | null; actorName: string; actorUpn: string; subjectName: string | null; subjectId: number | null }
interface EmergencyAccessRow extends AuditRow { hadData: boolean }
interface HrGroupChangeRow extends AuditRow { granted: boolean }
interface NewsletterUnread {
  edition: { id: number; issueNumber: number; title: string; publishedAt: string | null } | null;
  users: { id: number; name: string; email: string }[];
  note: string;
}
interface HrAuditsResponse {
  users: HrUserRow[];
  emergencyAccess: EmergencyAccessRow[];
  hrGroupChanges: HrGroupChangeRow[];
  newsletter: NewsletterUnread;
  generatedAt: string;
}

const fmtDate = (iso: string | null): string => (iso ? new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—");
const fmtDateTime = (iso: string | null): string => (iso ? new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");
const daysSince = (iso: string | null): number | null => (iso ? Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000) : null);

type TabKey = "invited" | "lapsed" | "emergency" | "hrgroup" | "unread" | "rejected" | "newsletter";
const TAB_LABELS: Record<TabKey, string> = {
  invited: "Not signed in",
  lapsed: "Inactive 30d+",
  emergency: "Emergency access",
  hrgroup: "HR-admin changes",
  unread: "Unread consents",
  rejected: "Rejected consents",
  newsletter: "Newsletter unread",
};
const TAB_ORDER: TabKey[] = ["invited", "lapsed", "emergency", "hrgroup", "unread", "rejected", "newsletter"];

// ── shared row + chip primitives (house style) ──────────────────────────────
function PersonRow({ name, email, isFirst, right, sub }: { name: string; email?: string; isFirst: boolean; right?: React.ReactNode; sub?: React.ReactNode }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", borderTop: isFirst ? "none" : `1px solid ${C.hairlineLt}` }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontFamily: F.body, fontWeight: 700, fontSize: 14, color: C.ink, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{name}</div>
        {email && <div style={{ fontFamily: F.mono, fontSize: 11.5, color: C.inkMute, fontWeight: 600, marginTop: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{email}</div>}
        {sub && <div style={{ fontFamily: F.body, fontSize: 12, color: C.inkSoft, fontWeight: 600, marginTop: 3 }}>{sub}</div>}
      </div>
      {right && <div style={{ flexShrink: 0, textAlign: "right" }}>{right}</div>}
    </div>
  );
}

function ConsentChip({ c }: { c: ConsentBrief }) {
  if (c.status === "read") return <Stamp tone="sage">{c.name} · read {fmtDate(c.at)}</Stamp>;
  if (c.status === "rejected") return <Stamp tone="signal">{c.name} · rejected {fmtDate(c.at)}</Stamp>;
  return <Stamp tone="brass">{c.name} · unread</Stamp>;
}

function EmptyTab({ children }: { children: React.ReactNode }) {
  return <div style={{ padding: "30px 16px", textAlign: "center", fontFamily: F.serif, fontStyle: "italic", fontSize: 14, color: C.inkMute }}>{children}</div>;
}

export function HrAuditsPage() {
  const navigate = useNavigate();
  const { me, loading: meLoading } = useCurrentUser();
  const [data, setData] = useState<HrAuditsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<TabKey>("invited");
  const [query, setQuery] = useState("");

  const allowed = Boolean(me && (me.isAdmin || me.isHrAdmin));

  const load = useCallback(async () => {
    try { setData(await apiFetch<HrAuditsResponse>("/api/hr-audits")); setError(null); }
    catch (e) { setError(e instanceof ApiError ? e.message : "Couldn't load HR audits."); }
  }, []);
  useEffect(() => { if (allowed) void load(); }, [allowed, load]);

  const users = useMemo(() => data?.users ?? [], [data]);
  const invited = useMemo(() => users.filter((u) => !u.lastLogin), [users]);
  const lapsed = useMemo(() => users.filter((u) => u.lastLogin && (daysSince(u.lastLogin) ?? 0) > 30).sort((a, b) => (a.lastLogin ?? "").localeCompare(b.lastLogin ?? "")), [users]);
  const unread = useMemo(() => users.filter((u) => u.consents.some((c) => c.status === "unread")), [users]);
  const rejected = useMemo(() => users.filter((u) => u.consents.some((c) => c.status === "rejected")), [users]);

  const q = query.trim().toLowerCase();
  const searchHits = useMemo(
    () => (q ? users.filter((u) => u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q)).slice(0, 20) : []),
    [q, users],
  );

  if (meLoading) return <PermissionLoading title="HR Audits" onBack={() => navigate("/apps")} />;

  if (!allowed) {
    return (
      <div>
        <TopBar title="HR Audits" onBack={() => navigate("/apps")} />
        <div style={{ padding: "18px 16px" }}>
          <Card>
            <div style={{ fontFamily: F.serif, fontSize: 17, fontWeight: 700, color: C.ink, marginBottom: 6 }}>This app is for admins and HR admins.</div>
            <div style={{ fontFamily: F.body, fontSize: 13, color: C.inkSoft, fontWeight: 600, lineHeight: 1.45 }}>Ask a system administrator if you need access to HR audit records.</div>
            <Button variant="secondary" fullWidth style={{ marginTop: 14 }} onClick={() => navigate("/apps")}>Back to apps</Button>
          </Card>
        </div>
      </div>
    );
  }

  const counts: Record<TabKey, number> = {
    invited: invited.length,
    lapsed: lapsed.length,
    emergency: data?.emergencyAccess.length ?? 0,
    hrgroup: data?.hrGroupChanges.length ?? 0,
    unread: unread.length,
    rejected: rejected.length,
    newsletter: data?.newsletter.users.length ?? 0,
  };

  return (
    <div>
      <TopBar title="HR Audits" sub="Staff records & audit trail" onBack={() => navigate("/apps")} />
      <div style={{ padding: "14px 16px 28px", display: "flex", flexDirection: "column", gap: 14 }}>
        {error && (
          <Card><div style={{ color: C.signalDk, fontFamily: F.body, fontWeight: 700 }}>{error}</div></Card>
        )}

        {/* ── Search: per-person consent status ── */}
        <Card>
          <Eyebrow>Find a person</Eyebrow>
          <div style={{ marginTop: 8 }}>
            <SearchInput value={query} onChange={setQuery} placeholder="Search a user to see their consent status…" />
          </div>
          {q && (
            <div style={{ marginTop: 12 }}>
              {searchHits.length === 0 ? (
                <div style={{ fontFamily: F.serif, fontStyle: "italic", fontSize: 13.5, color: C.inkMute, padding: "6px 2px" }}>No matching user.</div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  {searchHits.map((u) => (
                    <div key={u.id} style={{ background: C.paperWarm, borderRadius: 12, padding: "12px 14px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                        <span style={{ fontFamily: F.serif, fontSize: 16, fontWeight: 700, color: C.ink }}>{u.name}</span>
                        {u.isAdmin && <Stamp tone="brass">Admin</Stamp>}
                        {u.isHrAdmin && <Stamp tone="heather">HR Admin</Stamp>}
                        {u.isBlocked && <Stamp tone="signal">Blocked</Stamp>}
                      </div>
                      <div style={{ fontFamily: F.mono, fontSize: 11.5, color: C.inkMute, fontWeight: 600, marginTop: 2 }}>{u.email}</div>
                      <div style={{ fontFamily: F.body, fontSize: 12, color: C.inkSoft, fontWeight: 600, marginTop: 4 }}>
                        {u.authMethod.toUpperCase()} · {u.departments.join(", ") || "no department"} · last sign-in {fmtDate(u.lastLogin)}
                      </div>
                      <Hairline style={{ margin: "10px 0" }} />
                      <div style={{ fontFamily: F.body, fontSize: 12, fontWeight: 700, color: C.inkSoft, marginBottom: 7 }}>Consent documents</div>
                      {u.consents.length === 0 ? (
                        <div style={{ fontFamily: F.serif, fontStyle: "italic", fontSize: 13, color: C.inkMute }}>No consent documents apply to this person.</div>
                      ) : (
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
                          {u.consents.map((c) => <ConsentChip key={c.docId} c={c} />)}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </Card>

        {/* ── Tabs (hidden while searching) ── */}
        {!q && (
          <>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
              {TAB_ORDER.map((k) => {
                const on = tab === k;
                return (
                  <button
                    key={k}
                    onClick={() => setTab(k)}
                    style={{
                      display: "inline-flex", alignItems: "center", gap: 7, padding: "8px 12px", borderRadius: 999,
                      border: on ? `1.5px solid ${C.brassDk}` : `1px solid ${C.hairline}`, background: on ? C.brassDk : C.card,
                      color: on ? C.brassGlow : C.inkSoft, fontFamily: F.body, fontWeight: 800, fontSize: 12.5, cursor: "pointer", whiteSpace: "nowrap",
                    }}
                  >
                    {TAB_LABELS[k]}
                    <span style={{ fontFamily: F.mono, fontSize: 11, fontWeight: 700, padding: "1px 7px", borderRadius: 8, background: on ? "rgba(255,255,255,0.22)" : C.paperWarm, color: on ? C.brassGlow : C.inkMute }}>{counts[k]}</span>
                  </button>
                );
              })}
            </div>

            {!data && !error && <div aria-hidden="true" style={{ height: 280, background: C.paperWarm, borderRadius: 18, border: `1px solid ${C.hairlineLt}` }} />}

            {data && (
              <Card padded={false}>
                {tab === "invited" && (invited.length === 0 ? <EmptyTab>Everyone who's been invited has signed in.</EmptyTab> :
                  invited.map((u, i) => <PersonRow key={u.id} name={u.name} email={u.email} isFirst={i === 0} sub={`Invited ${fmtDate(u.createdAt)} · ${u.departments.join(", ") || "no department"}`} right={<Stamp tone="signal">Never signed in</Stamp>} />))}

                {tab === "lapsed" && (lapsed.length === 0 ? <EmptyTab>No one's been away for more than 30 days.</EmptyTab> :
                  lapsed.map((u, i) => <PersonRow key={u.id} name={u.name} email={u.email} isFirst={i === 0} sub={`Last sign-in ${fmtDate(u.lastLogin)}`} right={<Stamp tone="brass">{daysSince(u.lastLogin)} days ago</Stamp>} />))}

                {tab === "emergency" && ((data.emergencyAccess.length === 0) ? <EmptyTab>No emergency-contact access recorded.</EmptyTab> :
                  data.emergencyAccess.map((e, i) => <PersonRow key={e.id} name={`${e.actorName} viewed ${e.subjectName ?? "a user"}'s emergency contact`} isFirst={i === 0} sub={fmtDateTime(e.at)} right={<Stamp tone={e.hadData ? "heather" : "brass"}>{e.hadData ? "data shown" : "no data"}</Stamp>} />))}

                {tab === "hrgroup" && ((data.hrGroupChanges.length === 0) ? <EmptyTab>No HR Admin permission changes recorded.</EmptyTab> :
                  data.hrGroupChanges.map((e, i) => <PersonRow key={e.id} name={`${e.actorName} ${e.granted ? "granted HR Admin to" : "removed HR Admin from"} ${e.subjectName ?? "a user"}`} isFirst={i === 0} sub={fmtDateTime(e.at)} right={<Stamp tone={e.granted ? "sage" : "signal"}>{e.granted ? "Granted" : "Removed"}</Stamp>} />))}

                {tab === "unread" && (unread.length === 0 ? <EmptyTab>No one has unread consent documents.</EmptyTab> :
                  unread.map((u, i) => (
                    <div key={u.id} style={{ padding: "12px 14px", borderTop: i === 0 ? "none" : `1px solid ${C.hairlineLt}` }}>
                      <div style={{ fontFamily: F.body, fontWeight: 700, fontSize: 14, color: C.ink }}>{u.name}</div>
                      <div style={{ fontFamily: F.mono, fontSize: 11.5, color: C.inkMute, fontWeight: 600, marginBottom: 7 }}>{u.email}</div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>{u.consents.filter((c) => c.status === "unread").map((c) => <Stamp key={c.docId} tone="brass">{c.name}</Stamp>)}</div>
                    </div>
                  )))}

                {tab === "rejected" && (rejected.length === 0 ? <EmptyTab>No one has rejected a consent document.</EmptyTab> :
                  rejected.map((u, i) => (
                    <div key={u.id} style={{ padding: "12px 14px", borderTop: i === 0 ? "none" : `1px solid ${C.hairlineLt}` }}>
                      <div style={{ fontFamily: F.body, fontWeight: 700, fontSize: 14, color: C.ink }}>{u.name}</div>
                      <div style={{ fontFamily: F.mono, fontSize: 11.5, color: C.inkMute, fontWeight: 600, marginBottom: 7 }}>{u.email}</div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>{u.consents.filter((c) => c.status === "rejected").map((c) => <Stamp key={c.docId} tone="signal">{c.name} · {fmtDate(c.at)}</Stamp>)}</div>
                    </div>
                  )))}

                {tab === "newsletter" && (
                  <div>
                    <div style={{ padding: "12px 14px", borderBottom: `1px solid ${C.hairlineLt}` }}>
                      <div style={{ fontFamily: F.body, fontSize: 12.5, color: C.inkSoft, fontWeight: 600, lineHeight: 1.5 }}>
                        {data.newsletter.edition
                          ? <>Latest CEO newsletter: <b style={{ color: C.ink }}>#{data.newsletter.edition.issueNumber} {data.newsletter.edition.title}</b> · published {fmtDate(data.newsletter.edition.publishedAt)}.</>
                          : <>No published CEO newsletter yet.</>}
                        <div style={{ marginTop: 4, fontStyle: "italic", color: C.inkMute }}>{data.newsletter.note}</div>
                      </div>
                    </div>
                    {data.newsletter.users.length === 0
                      ? <EmptyTab>{data.newsletter.edition ? "Every recipient has opened it." : "Nothing to report."}</EmptyTab>
                      : data.newsletter.users.map((u, i) => <PersonRow key={u.id} name={u.name} email={u.email} isFirst={i === 0} right={<Stamp tone="brass">Not opened</Stamp>} />)}
                  </div>
                )}
              </Card>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default HrAuditsPage;
